import React, { useEffect, useState, useRef } from 'react';
import { Users, User, Search, Trash2, MessageSquare, Grid, List, Mic, Square, Loader, MapPin, Mail, Phone, Target, Building2, X, FileText, Edit } from 'lucide-react';
import { AddButton } from '@/components/ui/AddButton';
import { useAuthStore } from '@/stores/authStore';
import OpenStreetMapAddressPicker from '../../components/ui/GoogleMapsAddressPicker';
import { useAlert } from '../../components/AlertProvider';
import { useConfirm } from '../../components/ConfirmDialog';
import { useEmployeePermissions } from '../../hooks/useEmployeePermissions';
import api from '../../services/api';
import { PhoneInput } from '@/components/ui/PhoneInput';

interface Prospect {
  id: number;
  display_number?: string;
  company_name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string | null;
  maps_link: string | null;
  status: string;
  estimated_value: number | null;
  probability: number | null;
  next_action_date: string | null;
  converted: boolean;
  admin_id?: number;
  admin?: {
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
  siret?: string | null;
}

interface Apporteur {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface ProspectNote {
  id: number;
  content: string | null;
  note_type: string;
  audio_url?: string | null;
  transcription?: string | null;
  created_at: string;
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'Nouveau', color: 'bg-blue-100 text-blue-800' },
  { value: 'contacted', label: 'Contacté', color: 'bg-purple-100 text-purple-800' },
  { value: 'won', label: 'Gagné', color: 'bg-green-100 text-green-800' },
  { value: 'lost', label: 'Perdu', color: 'bg-red-100 text-red-800' },
];

const ProspectsTailwind: React.FC = () => {
  const { showError, showSuccess } = useAlert();
  const { showConfirm } = useConfirm();
  const currentUser = useAuthStore((state) => state.user);
  const { hasPermission } = useEmployeePermissions();
  const canCreate = hasPermission('prospects', 'create');
  const canUpdate = hasPermission('prospects', 'update');
  const canDelete = hasPermission('prospects', 'delete');

  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [apporteurs, setApporteurs] = useState<Apporteur[]>([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [apporteurFilter, setApporteurFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Prospect | null>(null);
  const [formStep, setFormStep] = useState<1 | 2>(1);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState<Prospect | null>(null);
  const [notes, setNotes] = useState<ProspectNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingProspect, setViewingProspect] = useState<Prospect | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Conversion modal state
  const [convertProspect, setConvertProspect] = useState<Prospect | null>(null);
  const [convertClientType, setConvertClientType] = useState<'pharmacie' | 'parapharmacie' | 'centrale' | 'magasin_bio' | 'therapeute'>('pharmacie');
  const [convertLoading, setConvertLoading] = useState(false);

  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    maps_link: '',
    siret: '',
    status: 'new',
    estimated_value: '',
    probability: '',
    next_action_date: '',
    admin_id: '',
  });

  useEffect(() => {
    loadProspects();
    if (currentUser?.role === 'super_admin') {
      loadApporteurs();
    }
  }, [statusFilter, apporteurFilter]);

  const loadApporteurs = async () => {
    try {
      const response = await api.get('/users/admins');
      setApporteurs(response.data || []);
    } catch (error) {
      console.error('Erreur chargement apporteurs', error);
    }
  };

  const loadProspects = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (currentUser?.role === 'super_admin' && apporteurFilter !== 'all') {
        params.append('admin_id', apporteurFilter);
      }
      const response = await api.get(`/prospects?${params.toString()}`);
      setProspects(response.data || []);
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = () => {
    setEditing(null);
    setFormData({
      company_name: '',
      contact_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postal_code: '',
      country: 'France',
      maps_link: '',
      siret: '',
      status: 'new',
      estimated_value: '',
      probability: '',
      next_action_date: '',
      admin_id: currentUser?.role === 'super_admin' ? '' : (currentUser?.id?.toString() || ''),
    });
    setShowModal(true);
  };

  const handleView = (prospect: Prospect) => {
    setViewingProspect(prospect);
    setShowViewModal(true);
  };

  const handleEdit = (prospect: Prospect) => {
    setEditing(prospect);
    setFormData({
      company_name: prospect.company_name,
      contact_name: prospect.contact_name || '',
      email: prospect.email || '',
      phone: prospect.phone || '',
      address: prospect.address || '',
      city: prospect.city || '',
      postal_code: prospect.postal_code || '',
      country: prospect.country || 'France',
      maps_link: prospect.maps_link || '',
      siret: prospect.siret || '',
      status: prospect.status,
      estimated_value: prospect.estimated_value?.toString() || '',
      probability: prospect.probability?.toString() || '',
      next_action_date: prospect.next_action_date ? prospect.next_action_date.split('T')[0] : '',
      admin_id: prospect.admin_id?.toString() || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.company_name.trim()) {
      showError('Le nom de l\'entreprise est requis');
      return;
    }

    if (currentUser?.role === 'super_admin' && !formData.admin_id) {
      showError('L\'apporteur d\'affaires est requis');
      return;
    }

    setBusy(true);
    try {
      const payload: any = {
        ...formData,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
        probability: formData.probability ? parseInt(formData.probability) : null,
        next_action_date: formData.next_action_date || null,
        admin_id: formData.admin_id ? parseInt(formData.admin_id) : null,
      };

      if (editing) {
        await api.put(`/prospects/${editing.id}`, payload);
        showSuccess('Prospect mis à jour');
      } else {
        await api.post('/prospects', payload);
        showSuccess('Prospect créé');
      }

      setShowModal(false);
      loadProspects();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (prospect: Prospect) => {
    const confirmed = await showConfirm({
      title: 'Confirmer',
      message: `Supprimer "${prospect.company_name}" ?`,
      confirmText: 'Supprimer'
    });
    if (!confirmed) return;

    try {
      await api.delete(`/prospects/${prospect.id}`);
      showSuccess('Prospect supprimé');
      loadProspects();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleConvert = (prospect: Prospect): void => {
    // Ouvrir directement la modale dédiée au choix du type de client
    setConvertClientType('pharmacie');
    setConvertProspect(prospect);
  };

  const handleCancelConvert = (): void => {
    if (convertLoading) return;
    setConvertProspect(null);
  };

  const handleConfirmConvert = async (): Promise<void> => {
    if (!convertProspect) return;
    setConvertLoading(true);
    try {
      const response = await api.post(
        `/prospects/${convertProspect.id}/convert?password=password123&client_type=${encodeURIComponent(convertClientType)}`
      );
      showSuccess(response.data.message);
      setConvertProspect(null);
      loadProspects();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur');
    } finally {
      setConvertLoading(false);
    }
  };


  const handleShowNotes = async (prospect: Prospect) => {
    setSelectedProspect(prospect);
    setShowNotesModal(true);
    try {
      const response = await api.get(`/prospects/${prospect.id}/notes`);
      setNotes(response.data || []);
    } catch (error: any) {
      showError('Erreur lors du chargement des notes');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedProspect) return;

    try {
      await api.post(`/prospects/${selectedProspect.id}/notes`, {
        prospect_id: selectedProspect.id,
        note_type: 'text',
        content: newNote,
      });
      setNewNote('');
      const response = await api.get(`/prospects/${selectedProspect.id}/notes`);
      setNotes(response.data || []);
      showSuccess('Note ajoutée');
    } catch (error: any) {
      showError('Erreur');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await handleVoiceNote(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      showError('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleVoiceNote = async (audioBlob: Blob) => {
    if (!selectedProspect) return;

    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice_note.webm');
      formData.append('prospect_id', selectedProspect.id.toString());

      await api.post(`/prospects/${selectedProspect.id}/notes/voice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const response = await api.get(`/prospects/${selectedProspect.id}/notes`);
      setNotes(response.data || []);
      showSuccess('Note vocale ajoutée et transcrite');
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setIsTranscribing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${option?.color || 'bg-gray-100 text-gray-800'}`}>
        {option?.label || status}
      </span>
    );
  };

  const getApporteurNameFromId = (id: number) => {
    const apporteur = apporteurs.find(a => a.id === id);
    if (!apporteur) return null;
    const name = [apporteur.first_name, apporteur.last_name].filter(Boolean).join(' ');
    return name || apporteur.email;
  };

  const getApporteurName = (item: Prospect) => {
    if (item.admin) {
      const name = [item.admin.first_name, item.admin.last_name].filter(Boolean).join(' ');
      return name || item.admin.email;
    }
    if (!item.admin_id) return null;
    return getApporteurNameFromId(item.admin_id);
  };


  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950">
      <div className="bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 rounded-xl px-4 sm:px-6 py-4 shadow-sm flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Prospects</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white dark:bg-gray-600 shadow' : ''}`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 shadow' : ''}`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
          {canCreate && (
            <AddButton
              onClick={handleCreate}
              className="w-full sm:w-auto"
            />
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher par nom, contact, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && loadProspects()}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            {currentUser?.role === 'super_admin' && (
              <select
                value={apporteurFilter}
                onChange={(e) => setApporteurFilter(e.target.value)}
                className="px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">Tous les apporteurs</option>
                {apporteurs.map((apporteur) => (
                  <option key={apporteur.id} value={apporteur.id.toString()}>
                    {getApporteurNameFromId(apporteur.id) || apporteur.email}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {busy ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : viewMode === 'list' ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50 dark:from-gray-800 dark:to-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Entreprise</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Coordonnées</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Apporteur</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {prospects.map((prospect) => (
                  <tr
                    key={prospect.id}
                    onClick={() => canUpdate ? handleEdit(prospect) : handleView(prospect)}
                    className="hover:bg-purple-50/30 dark:hover:bg-purple-900/10 transition-colors group cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                          {prospect.company_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {prospect.company_name}
                          </div>
                          <div className="mt-1">
                            {getStatusBadge(prospect.status)}
                          </div>
                          {prospect.siret && (
                            <div className="mt-2 space-y-0.5">
                              {prospect.siret && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                  <span className="font-medium">SIRET:</span> {prospect.siret}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <User className={`w-3.5 h-3.5 flex-shrink-0 ${prospect.contact_name ? 'text-emerald-500' : 'text-red-400'}`} />
                          <span className={`text-sm truncate ${prospect.contact_name ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500 italic'}`}>
                            {prospect.contact_name || 'Non renseigné'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className={`w-3.5 h-3.5 flex-shrink-0 ${prospect.phone ? 'text-emerald-500' : 'text-red-400'}`} />
                          <span className={`text-xs ${prospect.phone ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500 italic'}`}>
                            {prospect.phone || 'Non renseigné'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Mail className={`w-3.5 h-3.5 flex-shrink-0 ${prospect.email ? 'text-emerald-500' : 'text-red-400'}`} />
                          <span className={`text-xs truncate max-w-[180px] ${prospect.email ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500 italic'}`}>
                            {prospect.email || 'Non renseigné'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className={`w-3.5 h-3.5 flex-shrink-0 ${prospect.address || prospect.city ? 'text-emerald-500' : 'text-red-400'}`} />
                          <span className={`text-xs truncate max-w-[180px] ${prospect.address || prospect.city ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500 italic'}`}>
                            {prospect.city || prospect.address || 'Non renseignée'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`text-xs px-2 py-1 rounded-full inline-block ${prospect.admin_id || prospect.admin ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                        {getApporteurName(prospect) || 'Non assigné'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleShowNotes(prospect); }}
                          className="p-1.5 text-purple-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                          title="Notes"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                        {canUpdate && !prospect.converted && (
                          <button
                            onClick={(e) => { e.stopPropagation(); prospect.address && handleConvert(prospect); }}
                            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${prospect.address
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50 cursor-pointer'
                              : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'}`}
                            title={!prospect.address ? 'Adresse requise pour convertir' : 'Convertir en client'}
                          >
                            Convertir
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(prospect); }}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {prospects.map((prospect) => (
            <div key={prospect.id}
              onClick={() => canUpdate ? handleEdit(prospect) : handleView(prospect)}
              className="cursor-pointer bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 hover:shadow-lg hover:border-gray-200 dark:hover:border-gray-600 transition-all duration-200">
              {/* Header with Status */}
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate mb-1">
                    {prospect.company_name}
                  </h3>
                  {getStatusBadge(prospect.status)}
                </div>
              </div>

              {/* Contact Info - Always show fields with color indicator */}
              <div className="space-y-2 text-xs mb-4">
                <div className="flex items-center gap-2">
                  <User className={`w-3.5 h-3.5 flex-shrink-0 ${prospect.contact_name ? 'text-emerald-500' : 'text-red-400'}`} />
                  <span className={`truncate ${prospect.contact_name ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500 italic'}`}>
                    {prospect.contact_name || 'Non renseigné'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className={`w-3.5 h-3.5 flex-shrink-0 ${prospect.email ? 'text-emerald-500' : 'text-red-400'}`} />
                  <span className={`truncate ${prospect.email ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500 italic'}`}>
                    {prospect.email || 'Non renseigné'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className={`w-3.5 h-3.5 flex-shrink-0 ${prospect.phone ? 'text-emerald-500' : 'text-red-400'}`} />
                  <span className={`${prospect.phone ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500 italic'}`}>
                    {prospect.phone || 'Non renseigné'}
                  </span>
                </div>

                <div className="pt-2 mt-2 border-t border-gray-100 dark:border-gray-700/50 space-y-2">
                  <div className="flex items-center gap-2">
                    <MapPin className={`w-3.5 h-3.5 flex-shrink-0 ${prospect.address ? 'text-emerald-500' : 'text-red-400'}`} />
                    <span className={`truncate ${prospect.address ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500 italic'}`}>
                      {prospect.city || prospect.address || 'Non renseigné'}
                    </span>
                  </div>

                  {prospect.siret && (
                    <div className="space-y-1">
                      {prospect.siret && (
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <FileText className="w-3.5 h-3.5 text-purple-400" />
                          <span className="truncate">SIRET: {prospect.siret}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-3 px-1">
                <div className={`text-xs px-2 py-1 rounded-full inline-block ${prospect.admin_id || prospect.admin ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                  Apporteur : {getApporteurName(prospect) || 'Non assigné'}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 pt-3 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={(e) => { e.stopPropagation(); handleShowNotes(prospect); }}
                  className="p-1.5 text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                  title="Notes"
                >
                  <MessageSquare className="w-4 h-4" />
                </button>
                {canUpdate && !prospect.converted && (
                  <button
                    onClick={(e) => { e.stopPropagation(); prospect.address && handleConvert(prospect); }}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${prospect.address
                      ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50 cursor-pointer'
                      : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'}`}
                    title={!prospect.address ? 'Adresse requise pour convertir' : 'Convertir en client'}
                  >
                    Convertir
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(prospect); }}
                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg ml-auto transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <Target className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">{editing ? 'Modifier' : 'Nouveau'} Prospect</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Étape {formStep} sur 2</p>
                </div>
              </div>
              <button onClick={() => { setShowModal(false); setFormStep(1); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Step Indicator */}
            <div className="px-6 pt-4">
              <div className="flex items-center justify-center gap-4">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${formStep >= 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                    }`}>
                    <User className="w-5 h-5" />
                  </div>
                  <span className="text-xs mt-1 text-gray-600 dark:text-gray-400">Contact</span>
                </div>
                <div className={`w-16 h-1 rounded transition-colors ${formStep >= 2 ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${formStep >= 2 ? 'bg-purple-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                    }`}>
                    <Building2 className="w-5 h-5" />
                  </div>
                  <span className="text-xs mt-1 text-gray-600 dark:text-gray-400">Entreprise</span>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Step 1: Contact */}
              {formStep === 1 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-left-4 duration-300">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Contact *</label>
                      <input
                        type="text"
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Téléphone</label>
                      <PhoneInput
                        value={formData.phone}
                        onChange={(value) => setFormData({ ...formData, phone: value })}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Statut</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                      >
                        {STATUS_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {currentUser?.role === 'super_admin' && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Apporteur d'Affaires *</label>
                        <select
                          value={formData.admin_id}
                          onChange={(e) => setFormData({ ...formData, admin_id: e.target.value })}
                          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        >
                          <option value="">Sélectionner un apporteur...</option>
                          {apporteurs.map(apporteur => (
                            <option key={apporteur.id} value={apporteur.id}>
                              {apporteur.first_name} {apporteur.last_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Entreprise */}
              {formStep === 2 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div>
                    <label className="block text-sm font-medium mb-1">Entreprise *</label>
                    <input
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">SIRET</label>
                      <input
                        type="text"
                        value={formData.siret}
                        onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        placeholder="Ex: 123 456 789 00012"
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Adresse
                    </h3>
                    <OpenStreetMapAddressPicker
                      onAddressSelect={(addressData: any) => {
                        setFormData({
                          ...formData,
                          address: addressData.address || '',
                          city: addressData.city || '',
                          postal_code: addressData.postal_code || '',
                          country: addressData.country || 'France',
                          maps_link: addressData.maps_link || '',
                        });
                      }}
                      initialAddress={formData.address}
                      initialCity={formData.city}
                      initialPostalCode={formData.postal_code}
                      initialCountry={formData.country}
                      initialMapsLink={formData.maps_link}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  if (formStep === 1) {
                    setShowModal(false);
                    setFormStep(1);
                  } else {
                    setFormStep(1);
                  }
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {formStep === 1 ? 'Annuler' : 'Précédent'}
              </button>
              {formStep === 1 ? (
                <button
                  onClick={() => setFormStep(2)}
                  disabled={!formData.contact_name.trim()}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  Suivant
                </button>
              ) : (
                <button
                  onClick={handleSave}
                  disabled={busy}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors font-medium"
                >
                  {busy ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Convert Prospect Modal */}
      {convertProspect && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 text-yellow-700">
                !
              </span>
              Convertir en client
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Convertir
              <span className="font-semibold"> "{convertProspect.company_name}" </span>
              en client. Choisissez le type de client.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Type de client</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setConvertClientType('pharmacie')}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${convertClientType === 'pharmacie'
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                >
                  Pharmacie
                </button>
                <button
                  type="button"
                  onClick={() => setConvertClientType('parapharmacie')}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${convertClientType === 'parapharmacie'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                >
                  Parapharmacie
                </button>
                <button
                  type="button"
                  onClick={() => setConvertClientType('magasin_bio')}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${convertClientType === 'magasin_bio'
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                >
                  Magasin Bio
                </button>
                <button
                  type="button"
                  onClick={() => setConvertClientType('therapeute')}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${convertClientType === 'therapeute'
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                >
                  Thérapeute
                </button>
                {currentUser?.role === 'super_admin' && (
                  <button
                    type="button"
                    onClick={() => setConvertClientType('centrale')}
                    className={`col-span-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${convertClientType === 'centrale'
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                  >
                    Centrale d'Achat
                  </button>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={handleCancelConvert}
                disabled={convertLoading}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleConfirmConvert}
                disabled={convertLoading}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50"
              >
                {convertLoading ? 'Conversion...' : 'Convertir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Prospect Modal */}
      {showViewModal && viewingProspect && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">{viewingProspect.company_name}</h2>
                  </div>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
            {viewingProspect && (
              <div className="flex items-center justify-between">
                {getStatusBadge(viewingProspect.status)}
                {viewingProspect.converted && (
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                    Converti en client
                  </span>
                )}
              </div>
            )}

            {/* Admin/Apporteur */}
            {viewingProspect && viewingProspect.admin_id && (
              <div className="flex items-center gap-2 text-sm font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 px-3 py-2 rounded-lg w-fit">
                <Users className="w-4 h-4" />
                <span>Apporteur : {getApporteurName(viewingProspect)}</span>
              </div>
            )}

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column: Contact Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-700">
                    <User className="w-4 h-4 text-purple-500" />
                    Contact
                  </h3>

                  <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white dark:bg-gray-800 flex items-center justify-center shadow-sm text-emerald-500 flex-shrink-0">
                        <User className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Nom du contact</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {viewingProspect.contact_name || 'Non renseigné'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white dark:bg-gray-800 flex items-center justify-center shadow-sm text-blue-500 flex-shrink-0">
                        <Mail className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                        {viewingProspect.email ? (
                          <a href={`mailto:${viewingProspect.email}`} className="text-sm font-medium text-blue-600 hover:underline break-all">
                            {viewingProspect.email}
                          </a>
                        ) : (
                          <p className="text-sm text-gray-400 dark:text-gray-500 italic">Non renseigné</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white dark:bg-gray-800 flex items-center justify-center shadow-sm text-indigo-500 flex-shrink-0">
                        <Phone className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Téléphone</p>
                        {viewingProspect.phone ? (
                          <a href={`tel:${viewingProspect.phone}`} className="text-sm font-medium text-blue-600 hover:underline">
                            {viewingProspect.phone}
                          </a>
                        ) : (
                          <p className="text-sm text-gray-400 dark:text-gray-500 italic">Non renseigné</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Company Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-700">
                    <Building2 className="w-4 h-4 text-purple-500" />
                    Informations Entreprise
                  </h3>

                  <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 space-y-4">
                    {/* SIRET */}
                    {viewingProspect.siret && (
                      <div className="grid grid-cols-1 gap-3 pb-4 border-b border-gray-200 dark:border-gray-600/50">
                        {viewingProspect.siret && (
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-white dark:bg-gray-800 flex items-center justify-center shadow-sm text-purple-500 flex-shrink-0">
                              <FileText className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">SIRET</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {viewingProspect.siret}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Address */}
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white dark:bg-gray-800 flex items-center justify-center shadow-sm text-red-500 flex-shrink-0">
                        <MapPin className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Adresse</p>
                        {viewingProspect.address || viewingProspect.city ? (
                          <div className="text-sm text-gray-900 dark:text-white">
                            {viewingProspect.address && <div>{viewingProspect.address}</div>}
                            <div>
                              {[viewingProspect.postal_code, viewingProspect.city].filter(Boolean).join(' ')}
                            </div>
                            {viewingProspect.country && viewingProspect.country !== 'France' && (
                              <div>{viewingProspect.country}</div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 dark:text-gray-500 italic">Non renseignée</p>
                        )}

                        {viewingProspect.maps_link && (
                          <a
                            href={viewingProspect.maps_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline mt-2 inline-flex items-center gap-1"
                          >
                            Voir sur la carte <span className="text-[10px]">↗</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium"
                >
                  Fermer
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleShowNotes(viewingProspect);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium flex items-center gap-2"
                >
                  <MessageSquare className="w-4 h-4" />
                  Notes
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(viewingProspect);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Modifier
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && selectedProspect && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-2xl font-bold mb-6">Notes - {selectedProspect.company_name}</h2>

            <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
              {notes.map((note) => (
                <div key={note.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <span className={`text-xs px-2 py-1 rounded ${note.note_type === 'voice' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                      {note.note_type === 'voice' ? '🎤 Vocal' : '📝 Texte'}
                    </span>
                    <p className="text-xs text-gray-500">
                      {new Date(note.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                  {note.note_type === 'voice' && note.audio_url && (
                    <audio controls className="w-full mb-2">
                      <source src={note.audio_url} type="audio/webm" />
                    </audio>
                  )}
                  {note.transcription && (
                    <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded border-l-2 border-yellow-400">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Transcription:</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{note.transcription}</p>
                    </div>
                  )}
                  {note.content && (
                    <p className="text-sm text-gray-700 dark:text-gray-300">{note.content}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <div className="flex gap-2">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Nouvelle note texte..."
                  rows={3}
                  className="flex-1 px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                />
                <div className="flex flex-col gap-2">
                  {!isRecording ? (
                    <button
                      onClick={startRecording}
                      disabled={isTranscribing}
                      className="p-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      title="Enregistrer une note vocale"
                    >
                      <Mic className="w-5 h-5" />
                    </button>
                  ) : (
                    <button
                      onClick={stopRecording}
                      className="p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 animate-pulse"
                      title="Arrêter l'enregistrement"
                    >
                      <Square className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
              {isTranscribing && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Loader className="w-4 h-4 animate-spin" />
                  Transcription en cours...
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowNotesModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Fermer
                </button>
                <button
                  onClick={handleAddNote}
                  disabled={!newNote.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Ajouter note texte
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProspectsTailwind;
